<template>
  <div class="ktb-result-container success-page">
    <div class="navbar navbar-expand navbar-light bg-primary mb-4">
      <div class="container">
        <span class="navbar-brand text-white">
          <i class="fas fa-credit-card mr-2"></i>KTB Fast Pay
        </span>
      </div>
    </div>

    <div class="container py-4">
      <b-row class="justify-content-center">
        <b-col cols="12" md="8" lg="6">
          <b-card class="shadow-sm">
            <div class="text-center mb-4">
              <div class="success-icon mb-3">
                <i class="fas fa-check-circle"></i>
              </div>
              <h2 class="mb-3">การชำระเงินสำเร็จ</h2>
              <p class="lead text-muted">
                ระบบได้รับการชำระเงินของคุณเรียบร้อยแล้ว
              </p>
            </div>

            <!-- Loading state -->
            <div v-if="isLoading" class="text-center py-4 mb-4">
              <b-spinner variant="primary" label="กำลังโหลดข้อมูล"></b-spinner>
              <p class="mt-2">กำลังดึงข้อมูลการทำรายการ...</p>
            </div>

            <!-- Error state -->
            <div v-else-if="errorMessage" class="alert alert-warning mb-4">
              <i class="fas fa-exclamation-triangle mr-2"></i>
              {{ errorMessage }}
              <div class="text-right mt-2">
                <b-button size="sm" variant="outline-primary" @click="fetchTransactionDetailsData">
                  <i class="fas fa-sync-alt mr-1"></i> ลองอีกครั้ง
                </b-button>
              </div>
            </div>

            <!-- Success state with transaction details -->
            <template v-else>

              <!-- Payment Details -->
              <div class="payment-summary p-3 bg-light rounded mb-4">
                <h5 class="font-weight-bold mb-3">รายละเอียดการชำระเงิน</h5>
                <div class="d-flex justify-content-between mb-2">
                  <span>จำนวนเงิน:</span>
                  <strong>{{ formatCurrency(amount || getOrderAmount) }}</strong>
                </div>
                <div class="d-flex justify-content-between mb-2">
                  <span>วันที่ทำรายการ:</span>
                  <strong>{{ formatDate(paymentDate || dateTime) }}</strong>
                </div>
                <div class="d-flex justify-content-between mb-2">
                  <span>สถานะ:</span>
                  <strong class="text-success">สำเร็จ</strong>
                </div>
                <div v-if="orderType" class="d-flex justify-content-between mb-2">
                  <span>ประเภทรายการ:</span>
                  <strong>{{ getOrderTypeText }}</strong>
                </div>
                <div v-if="storageEndDate" class="d-flex justify-content-between mb-2">
                  <span>วันหมดอายุ:</span>
                  <strong>{{ formatDate(storageEndDate) }}</strong>
                </div>
                <!-- Added reference numbers to this section -->
                <div v-if="ktbReferenceId" class="d-flex justify-content-between mb-2">
                  <span>หมายเลขอ้างอิง KTB:</span>
                  <strong>{{ ktbReferenceId }}</strong>
                </div>
                <div v-if="dentCloudReferenceId" class="d-flex justify-content-between">
                  <span>หมายเลขอ้างอิง DentCloud:</span>
                  <strong>{{ dentCloudReferenceId }}</strong>
                </div>
              </div>

              <!-- Action Buttons -->
              <div class="text-center">
                <b-button variant="primary" @click="closeWindow">
                  <i class="fas fa-check mr-1"></i> ปิดหน้านี้
                </b-button>
              </div>
            </template>
          </b-card>
        </b-col>
      </b-row>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import moment from "moment";

export default {
  name: "KtbFastPaySuccess",
  data() {
    return {
      amount: 0,
      dateTime: new Date(),
      transactionId: null,
      referenceId: null,
      copyStatus: "",
      showCopySuccess: false,
      isLoading: false,
      errorMessage: null,

      // Additional fields from API
      dentCloudReferenceId: null,
      ktbReferenceId: null,
      paymentDate: null,
      orderType: null,
      storageEndDate: null,
    };
  },
  computed: {
    ...mapGetters({
      getBranchInfo: "moduleUser/getBranchInfo",
      transactionDetails: "modulePayment/getTransactionDetails",
      transactionDetailsLoading: "modulePayment/getTransactionDetailsLoading",
      transactionDetailsError: "modulePayment/getTransactionDetailsError",
    }),

    getOrderAmount() {
      if (this.transactionDetails && this.transactionDetails.order) {
        return this.transactionDetails.order.totalBahts;
      } else if (this.transactionDetails && this.transactionDetails.payment) {
        return this.transactionDetails.payment.amountBahts;
      }
      return 0;
    },

    getOrderTypeText() {
      if (!this.orderType) return '';

      const typeMap = {
        'storage_subscription': 'สมัครแพ็คเกจพื้นที่จัดเก็บ',
        'credit_topup': 'เติมเงินเครดิต',
        'subscription': 'สมัครแพ็คเกจบริการ',
      };

      return typeMap[this.orderType] || this.orderType;
    }
  },
  watch: {
    transactionDetailsLoading(newValue) {
      this.isLoading = newValue;
    },
    transactionDetailsError(newValue) {
      this.errorMessage = newValue;
    },
    transactionDetails(newValue) {
      if (newValue) {
        this.updateTransactionDetails(newValue);
      }
    }
  },
  methods: {
    ...mapActions({
      fetchTransactionDetails: "modulePayment/fetchTransactionDetails",
    }),

    formatCurrency(value) {
      if (!value && value !== 0) return "฿0.00";
      return `฿${parseFloat(value).toLocaleString("th-TH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    },

    formatDate(date) {
      if (!date) return "-";
      return moment(date).locale("th").format("DD MMM YYYY, HH:mm:ss");
    },

    closeWindow() {
      // Just try to close the window/tab and don't redirect
      try {
        window.close();
      } catch (e) {
        console.error("Error closing window:", e);
      }
    },

    viewTransactionHistory() {
      const { clinicUrl, branchUrl } = this.$route.params;
      if (clinicUrl && branchUrl) {
        window.open(`/${clinicUrl}/${branchUrl}/subscription`, '_blank');
      }
    },

    copyToClipboard(text) {
      if (!text) return;

      navigator.clipboard
        .writeText(text)
        .then(() => {
          this.$bvToast.toast("คัดลอกไปยังคลิปบอร์ดแล้ว", {
            title: "สำเร็จ",
            variant: "success",
            solid: true,
            autoHideDelay: 2000,
          });
        })
        .catch(err => {
          console.error("Failed to copy: ", err);
          this.$bvToast.toast("ไม่สามารถคัดลอกได้", {
            title: "ข้อผิดพลาด",
            variant: "danger",
            solid: true,
            autoHideDelay: 2000,
          });
        });
    },

    parseQueryParams() {
      // Extract transaction information from URL query parameters
      const {
        amount,
        transactionId,
        dateTime,
        Ref, // KTB reference ID
      } = this.$route.query;

      if (amount) {
        this.amount = parseFloat(amount);
      }

      if (transactionId) {
        this.transactionId = transactionId;
      }

      if (Ref) {
        this.referenceId = Ref;
      } else if (this.$route.query.ref) {
        this.referenceId = this.$route.query.ref;
      }

      if (dateTime) {
        this.dateTime = new Date(dateTime);
      } else {
        this.dateTime = new Date();
      }
    },

    async fetchTransactionDetailsData() {
      // Set loading state - hide any previous error
      this.errorMessage = null;
      this.isLoading = true;

      const { clinicUrl, branchUrl } = this.$route.params;
      const referenceId = this.referenceId || this.$route.query.ref;

      if (!clinicUrl || !branchUrl || !referenceId) {
        // Don't show missing data error to user
        this.isLoading = false;
        return;
      }

      try {
        await this.fetchTransactionDetails({
          clinicUrl,
          branchUrl,
          referenceId
        });
      } catch (error) {
        // Just log to console, but don't display error to user
        console.error("Error fetching transaction details:", error);
      } finally {
        this.isLoading = false;
      }
    },

    updateTransactionDetails(data) {
      if (!data) return;

      // Extract payment information
      if (data.payment) {
        this.dentCloudReferenceId = data.payment.dentCloudReferenceId;
        this.ktbReferenceId = data.payment.ktbFastPayReferenceId;
        this.paymentDate = data.payment.createdAt;
      }

      // Extract order information
      if (data.order) {
        this.orderType = data.order.type;
        this.storageEndDate = data.order.storageEndDate;
      }
    }
  },
  created() {
    this.parseQueryParams();
    this.fetchTransactionDetailsData();
  },
};
</script>

<style scoped>
.ktb-result-container {
  min-height: 100vh;
  background-color: #f8f9fa;
  display: flex;
  flex-direction: column;
}

.navbar {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.container {
  flex-grow: 1;
}

.success-icon {
  font-size: 5rem;
  color: #28a745;
  animation: pulse 1.5s ease-in-out;
}

@keyframes pulse {
  0% {
    transform: scale(0.8);
    opacity: 0;
  }
  50% {
    transform: scale(1.2);
    opacity: 1;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

.payment-summary, .clinic-info {
  border: 1px solid #dee2e6;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.transaction-details {
  border: 2px solid #28a745;
  border-radius: 8px;
  background-color: rgba(40, 167, 69, 0.05);
  transition: all 0.3s ease;
}

.transaction-details:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.clinic-info {
  background-color: rgba(52, 144, 220, 0.05);
}

@media (max-width: 768px) {
  .container {
    padding-top: 1rem !important;
    padding-bottom: 1rem !important;
  }

  .success-icon {
    font-size: 4rem;
  }
}
</style>